import { Vat } from '@app/components/Price/Price';
import {
  Box,
  constants,
  Skeleton,
  Stack,
  Text,
} from '@redbubble/design-system';
import { FormattedNumber, IntlShape, useIntl } from '@redbubble/next/intl';
import Image from 'next/legacy/image';
import React from 'react';
import success from '../../public/icons/success.svg';
import type { CouponType, ShippingPreference } from '../../types/monolith/cart';
import styles from './Costs.module.scss';
import { messages } from './messages';
import { getShippingTitle } from './shippingTitle';

const Loading = () => {
  const intl = useIntl();
  return (
    <Box
      className={styles.wrapper}
      element="ul"
      aria-label={intl.formatMessage(messages.costSummaryText)}
      data-testid="costs-loading"
    >
      <Skeleton height={24} animated roundedCorners />
      <Skeleton height={24} animated roundedCorners />
      <Skeleton height={32} animated roundedCorners />
    </Box>
  );
};

const FreeOrderMessage = () => {
  const intl = useIntl();
  return (
    <Box role="alert" className={styles.freeOrderMessage}>
      <Image
        width={48}
        height={48}
        layout="fixed"
        src={success}
        alt=""
        className={styles.image}
        aria-hidden
      />
      <Stack>
        <Text type="display5">
          {intl.formatMessage(messages.freeOrderMessageTitle)}
        </Text>
        <Text type="body2">
          {intl.formatMessage(messages.freeOrderMessageSubtitle)}
        </Text>
      </Stack>
    </Box>
  );
};

export interface CostsProps {
  currency: string;

  subtotal: number;
  undiscountedSubtotal: number;
  total: number;

  taxAmount?: number;
  showTaxSeparately?: boolean;
  isTaxComputed: boolean;

  shippingMethod: ShippingPreference;
  shippingAmountExcludingTax: number;
  shippingAmountWithTax: number;
  shippingEstimateTaxAmount: number;

  flatRateApplied?: boolean;
  digitalProductsOnly?: boolean;
  lineItemsCount: number;
  appliedCoupon?: {
    code: string;
    type: CouponType;
    discountAmount: number;
  };
  /**
   * When set to true, this indicates the shipping tax is included in the shipping amount and we should not include it in the "Tax" row
   * https://redbubble.atlassian.net/wiki/spaces/PEX/pages/3422683182/Costs+Component+Logic+Breakdown
   */
  includeShippingTaxWithShippingAmount?: boolean;
  pendingTaxText: string;
  isShippingEstimated?: boolean;
  showSubtotalOnly?: boolean;
}

export const isGiftCardOrAmountCoupon = (type: CouponType) =>
  type === 'GiftCertificateCoupon' || type === 'AmountCoupon';

// NOTE(cy): when tax has not been fully computed yet, we don't want to tell
// the users that the cart total amount that is the _final_ total. So,
// when tax is not fully calculated, we'll display the text 'Subtotal' in place of 'Total'.
export const getTotalsTitle = (intl: IntlShape, isTaxComputed: boolean) => {
  return intl.formatMessage(
    isTaxComputed ? messages.totalTitle : messages.subtotalTitle
  );
};

export const Costs = ({
  currency,
  subtotal,
  undiscountedSubtotal,
  total,
  taxAmount,
  showTaxSeparately,
  shippingMethod,
  shippingAmountExcludingTax,
  shippingAmountWithTax,
  shippingEstimateTaxAmount,
  flatRateApplied,
  digitalProductsOnly,
  lineItemsCount,
  appliedCoupon,
  includeShippingTaxWithShippingAmount,
  pendingTaxText,
  isTaxComputed,
  isShippingEstimated,
  showSubtotalOnly = false,
}: CostsProps) => {
  const intl = useIntl();
  const shippingTitle = getShippingTitle({
    intl,
    shippingMethod,
    isShippingEstimated,
    flatRateApplied,
  });

  const isShowingUndiscountedSubtotal = undiscountedSubtotal > subtotal;

  const appliedCouponisGiftcardOrAmount =
    appliedCoupon && isGiftCardOrAmountCoupon(appliedCoupon.type);

  const { displayTaxAmount, displayShippingAmount } = (() => {
    let displayTaxAmount = taxAmount;
    let displayShippingAmount = showTaxSeparately
      ? shippingAmountExcludingTax
      : shippingAmountWithTax;

    // The "Tax" line shows after we estimate with a wallet/PayPal address, `includeShippingTaxWithShippingAmount` will be true.
    // So we:
    // - include the shipping tax in the "Shipping" line so it matches the modal totals
    // - exclude the shipping tax from the "Tax" line so the summary adds up
    if (
      includeShippingTaxWithShippingAmount &&
      showTaxSeparately &&
      isTaxComputed
    ) {
      displayShippingAmount += shippingEstimateTaxAmount;
      displayTaxAmount = (displayTaxAmount || 0) - shippingEstimateTaxAmount;
    }

    return {
      displayTaxAmount,
      displayShippingAmount,
    };
  })();

  const showTaxLine =
    !showSubtotalOnly &&
    showTaxSeparately &&
    isTaxComputed &&
    displayTaxAmount !== undefined;

  const totalsTitle = showSubtotalOnly
    ? intl.formatMessage(messages.subtotalTitle)
    : getTotalsTitle(intl, isTaxComputed);

  const totalSubtitle = (() => {
    if (showSubtotalOnly && showTaxSeparately) {
      return intl.formatMessage(messages.taxAndShippingAtCheckout);
    }

    if (showTaxSeparately && !isTaxComputed) {
      return pendingTaxText;
    }

    if (
      !showSubtotalOnly &&
      !showTaxSeparately &&
      isTaxComputed &&
      displayTaxAmount !== undefined
    ) {
      // Pre-formatting the amount as a string first, then combining with text, so that we can have it wrapped in a single <span> tag without unwrapped text nodes.
      // This is crucial to avoid React errors when Google Translate and similar tools modify the DOM.
      // See https://github.com/facebook/react/issues/11538#issuecomment-390386520
      const formattedTaxAmount = intl.formatNumber(displayTaxAmount, {
        currency: currency,
        style: 'currency',
      });

      const fullMessage = intl.formatMessage(messages.includedTax, {
        taxAmount: formattedTaxAmount,
      });

      return <span>{fullMessage}</span>;
    }
  })();

  const couponValue = appliedCouponisGiftcardOrAmount
    ? -appliedCoupon.discountAmount
    : 0;
  const displayedSubtotal = subtotal + couponValue;
  const displayedTotal = showSubtotalOnly ? displayedSubtotal : total;

  return (
    <Box
      className={styles.wrapper}
      element="ul"
      aria-label={intl.formatMessage(messages.costSummaryText)}
    >
      <Stack
        direction={constants.HORIZONTAL}
        justifyContent="space-between"
        aria-label={intl.formatMessage(messages.subtotalTitle)}
        element="li"
      >
        <Box>
          <Text marginRight="xs">
            {intl.formatMessage(messages.itemsTitle, {
              lineItemsCount,
            })}
          </Text>
          {showSubtotalOnly && !showTaxSeparately && <Vat size="small" />}
        </Box>

        <div>
          {isShowingUndiscountedSubtotal && (
            <Text muted marginRight="xs">
              <del>
                <span className="clipped">
                  {intl.formatMessage(messages.previously)}
                </span>
                <span>
                  <FormattedNumber
                    value={undiscountedSubtotal}
                    currency={currency}
                    style="currency"
                  />
                </span>
              </del>
            </Text>
          )}
          {isShowingUndiscountedSubtotal && (
            <span className="clipped">{intl.formatMessage(messages.now)}</span>
          )}
          <span>
            <FormattedNumber
              value={subtotal}
              currency={currency}
              style="currency"
            />
          </span>
        </div>
      </Stack>

      {!digitalProductsOnly && !showSubtotalOnly && (
        <Stack
          direction={constants.HORIZONTAL}
          justifyContent="space-between"
          element="li"
          data-testid="costs-shipping"
        >
          <Text>{shippingTitle}</Text>
          <FormattedNumber
            value={displayShippingAmount}
            currency={currency}
            style="currency"
          />
        </Stack>
      )}

      {showTaxLine && (
        <Stack
          direction={constants.HORIZONTAL}
          justifyContent="space-between"
          element="li"
          aria-label={intl.formatMessage(messages.taxTitle)}
          data-testid="costs-tax"
        >
          <Text>{intl.formatMessage(messages.taxTitle)}</Text>
          <FormattedNumber
            value={displayTaxAmount}
            currency={currency}
            style="currency"
          />
        </Stack>
      )}

      {appliedCouponisGiftcardOrAmount && (
        <Stack
          direction={constants.HORIZONTAL}
          justifyContent="space-between"
          element="li"
        >
          <Text>
            {appliedCoupon && appliedCoupon.type === 'GiftCertificateCoupon'
              ? intl.formatMessage(messages.giftcardCoupon)
              : intl.formatMessage(messages.amountCoupon)}
          </Text>
          <FormattedNumber
            value={couponValue}
            currency={currency}
            style="currency"
          />
        </Stack>
      )}

      <Stack
        direction={constants.HORIZONTAL}
        justifyContent="space-between"
        element="li"
        aria-label={totalsTitle}
      >
        <Box>
          <Text type="display4" display="block">
            {totalsTitle}
          </Text>
          {totalSubtitle && (
            <Text type="caption" display="block" muted>
              {totalSubtitle}
            </Text>
          )}
        </Box>

        <Text type="display3" textAlign={constants.RIGHT}>
          <FormattedNumber
            value={displayedTotal}
            currency={currency}
            style="currency"
          />
        </Text>
      </Stack>

      {total === 0 && <FreeOrderMessage />}
    </Box>
  );
};

Costs.Loading = Loading;
